/**
 * Round 135 - calls to the PUBLIC backend routes (/api/v1/public/*): no account, no ID token.
 * Used before sign-up: the specialty picker, a specialty's description page, the legal pages and the visitor's
 * "My specialty isn't listed" request + its e-mail confirmation.
 *
 * Deliberately NOT apiFetch: apiFetch needs a signed-in user and splices the preview prefix in. The public routes are
 * mounted outside /preview and never run inside an admin "preview as learner" (they carry no learner state), so this
 * file is a documented exception in AdminLessonPreview.test.tsx's previewPrefix guard.
 */
import { isRateLimitedBody, notifyRateLimited, parseRetryAfterSeconds } from '@/core/rateLimit';
import { ApiFetchError } from '@/core/apiProxy';
import { trackFirstCall } from '@/core/warmingNotice';

const API_BASE = import.meta.env.VITE_API_BASE_URL || '/api/v1';

async function publicFetch<T>(path: string, options: RequestInit = {}): Promise<T> {
  const res = await trackFirstCall(fetch(`${API_BASE}/public${path}`, {
    ...options,
    headers: { 'Content-Type': 'application/json', ...(options.headers ?? {}) },
  }));
  let json: any = null;
  try { json = await res.json(); } catch { /* not JSON: handled below */ }
  if (json && isRateLimitedBody(res.status, json)) notifyRateLimited(parseRetryAfterSeconds(json, res.headers.get('Retry-After')));
  if (!res.ok || !json?.success) throw new ApiFetchError(json?.error || `HTTP ${res.status}`, res.status, json?.code, json?.data);
  return json.data as T;
}

export interface PublicSpecialty { id: string; name: string; tagline: string | null; hasDescription: boolean; lessons: number }
export interface PublishedPage {
  pageKey: string; lang: 'en' | 'ar'; requestedLang: 'en' | 'ar'; fallback: boolean; version: number; title: string | null; html: string; publishedAt: string | null;
}

const langOf = (lang: string) => (lang.startsWith('ar') ? 'ar' : 'en');

export async function getPublicSpecialties(lang: string): Promise<PublicSpecialty[]> {
  return (await publicFetch<{ specialties: PublicSpecialty[] }>(`/specialties?lang=${langOf(lang)}`)).specialties;
}

/** null when the page has not been published (404 PAGE_NOT_PUBLISHED). */
export async function getPublicContentPage(pageKey: string, lang: string): Promise<PublishedPage | null> {
  try {
    return await publicFetch<PublishedPage>(`/content-pages/${encodeURIComponent(pageKey)}?lang=${langOf(lang)}`);
  } catch (e) {
    if (e instanceof ApiFetchError && e.status === 404) return null;
    throw e;
  }
}

export async function sendVisitorSpecialtyRequest(input: { name: string; email: string; specialty: string; consent: boolean; lang: string }): Promise<void> {
  await publicFetch('/specialty-requests', { method: 'POST', body: JSON.stringify({ ...input, lang: langOf(input.lang) }) });
}

export async function confirmVisitorSpecialtyRequest(token: string): Promise<{ specialty: string }> {
  return publicFetch<{ specialty: string }>('/specialty-requests/confirm', { method: 'POST', body: JSON.stringify({ token }) });
}

/**
 * Round 139d - "Forgot password". The server decides by how the account signs in (a reset link, a "use Continue with Google" e-mail, or
 * nothing) and answers the same 202 whatever the address, so this resolves the same way for every address (no account enumeration).
 */
export async function requestPasswordHelp(email: string, lang: string): Promise<void> {
  await publicFetch('/password-help', { method: 'POST', body: JSON.stringify({ email: email.trim(), lang: langOf(lang) }) });
}

/**
 * Round 141 - "Sign up with email". The SERVER creates the account (so its per-IP limit, the disposable-domain blocklist and the
 * "this address already has an account" answers apply) and sends our confirmation e-mail; the browser then signs in with the password.
 * Refusals arrive as ApiFetchError codes: EMAIL_USES_GOOGLE, EMAIL_HAS_ACCOUNT, DISPOSABLE_EMAIL, INVALID_EMAIL, WEAK_PASSWORD, RATE_LIMITED.
 */
export async function signUpWithEmail(input: { email: string; password: string; lang: string }): Promise<{ created: boolean; emailSent: boolean }> {
  return publicFetch('/sign-up', { method: 'POST', body: JSON.stringify({ email: input.email.trim(), password: input.password, lang: langOf(input.lang) }) });
}

// ── The specialty chosen BEFORE sign-up ───────────────────────────────────────
// Kept in this browser only until the profile step saves it (the server then freezes it: firestore.rules). It is a
// convenience, not a record: the profile step re-checks it against the live list.
const CHOSEN_KEY = 'emi.chosenSpecialty';

export function rememberChosenSpecialty(fieldId: string): void {
  try { localStorage.setItem(CHOSEN_KEY, fieldId); } catch { /* private window: the profile step asks again */ }
}
export function readChosenSpecialty(): string | null {
  try { return localStorage.getItem(CHOSEN_KEY); } catch { return null; }
}
export function forgetChosenSpecialty(): void {
  try { localStorage.removeItem(CHOSEN_KEY); } catch { /* ignore */ }
}
